/**
 * Utilities for managing the state of the Lia chat in localStorage/sessionStorage
 */

// Storage keys
export const CHAT_OPEN_STATE_KEY = "lia-chat-open-state";
export const CHAT_SCROLLED_TO_START_KEY = "lia-chat-scrolled-to-start";
// Stores which conversation the user is currently viewing.
// Must not survive logout (handled by clearAllChatData()).
export const CHAT_ACTIVE_CONVERSATION_ID_KEY =
  "lia-chat-active-conversation-id";

/**
 * Maps conversationId → agent_type, persisted in localStorage so the history
 * panel can group conversations by agent even across page reloads.
 */
const CHAT_CONVERSATION_AGENT_TYPES_KEY = "lia-chat-conversation-agent-types";

export function getConversationAgentTypes(): Record<string, string> {
  try {
    const raw = localStorage.getItem(CHAT_CONVERSATION_AGENT_TYPES_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return {};
  }
}

export function setConversationAgentType(
  conversationId: string,
  agentType: string,
): void {
  try {
    const current = getConversationAgentTypes();
    current[conversationId] = agentType;
    localStorage.setItem(
      CHAT_CONVERSATION_AGENT_TYPES_KEY,
      JSON.stringify(current),
    );
  } catch {
    // ignore storage errors
  }
}

function clearConversationAgentTypes(): void {
  try {
    localStorage.removeItem(CHAT_CONVERSATION_AGENT_TYPES_KEY);
  } catch {
    // ignore
  }
}

// When true, the next login should show the default greeting instead of the
// last conversation (requirement: reset after logout+login).
export const CHAT_RESET_NEXT_LOGIN_KEY = "lia-chat-reset-next-login";

export function getInitialChatState(): true | false | undefined {
  const savedState = localStorage.getItem(CHAT_OPEN_STATE_KEY);
  if (savedState === null) return true;
  if (savedState === "false") return false;
  return undefined;
}

/**
 * Save the chat state to localStorage
 */
export function saveChatState(isOpen: boolean): void {
  localStorage.setItem(CHAT_OPEN_STATE_KEY, String(isOpen));
}

/**
 * Clear the chat state from localStorage
 * Used during logout to ensure that the chat opens automatically in the next session
 */
export function clearChatState(): void {
  localStorage.removeItem(CHAT_OPEN_STATE_KEY);
}

/**
 * Check if there is saved chat state in localStorage
 */
export function hasChatState(): boolean {
  return localStorage.getItem(CHAT_OPEN_STATE_KEY) !== null;
}

/**
 * Check if scroll to the start of the chat has already been done in this session
 */
export function hasScrolledToStart(): boolean {
  return sessionStorage.getItem(CHAT_SCROLLED_TO_START_KEY) === "true";
}

/**
 * Mark that scroll to the start of the chat has already been done in this session
 */
export function markScrolledToStart(): void {
  sessionStorage.setItem(CHAT_SCROLLED_TO_START_KEY, "true");
}

/**
 * Clear the scroll to the start of the chat flag
 */
export function clearScrolledToStart(): void {
  sessionStorage.removeItem(CHAT_SCROLLED_TO_START_KEY);
}

/**
 * Clear all chat information (state and scroll)
 * Used during logout
 */
export function clearAllChatData(): void {
  clearChatState();
  clearScrolledToStart();
  clearConversationAgentTypes();

  try {
    // Remove current conversation selection (chat session ended).
    sessionStorage.removeItem(CHAT_ACTIVE_CONVERSATION_ID_KEY);
  } catch {
    // ignore
  }

  // Force greeting on next login.
  try {
    localStorage.setItem(CHAT_RESET_NEXT_LOGIN_KEY, "true");
  } catch {
    // ignore
  }
}
